<script>
	import { postUploadData, getData } from "../helpers.js";
	import Navbar from "../components/Navbar.svelte";
	import Footer from "../components/Footer.svelte";
	
	let [formatStyle, weekEnding, targetAgency, targetOrganization, formatTest, useOCR] = ['', 'Friday', '', '', false, false];
	let [formatStyleError, weekEndingError, targetOrganizationError] = ['', '', '', ''];
	let formatWarning = ''; // For unmapped combinations
	let showUploadDropdown = false;
	let privateCPRFeature = false

	getData('user/paywall-features').then(response => response.json()).then(ret_json => {
		if (ret_json.status === 'success') {
			privateCPRFeature = ret_json.features.includes('PRIVATE_SEARCH');
		}
	});


	const format_styles = [
		'PWC Style',
		'LCP Style',
		'WH-347 Style',
		'DIR PWC',
		'old DLSE PWC Style', // legacy format
		// 'Generic - Unknown Style', // commented for future use
	];

	const week_ending_options = [
		'Friday',
		'Saturday', 
		'Sunday',
		'Monday',
		'Tuesday',
	];

	const file_format_options = [
		'Generic Text PDF/A',
		'Image Scan PDF',
		//'TreeRoot PDF/A',
	];
	
	let otherError = '';
	let searchForm;
	let db_form_desktop;
   	let db_form_mobile;
	//let db_form;
	

	let fileInput = null;
	let fileInput_m = null;

	let uploadStatus = "idle"; // Initial state
	let uploadedFile = null;
	let uploadMessage = "";

	const fileType = ".pdf"

	let submitted = false;


	let recentSubmissions = new Map(); // fingerprint -> timestamp
	const DEDUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

	function getSubmissionFingerprint(files, formatStyle, targetOrganization) {
		if (!files || files.length === 0) return null;
		const f = files[0];
		return `${f.name}|${f.size}|${f.lastModified}|${formatStyle}|${targetOrganization}`;
	}

	function checkDuplicate(fingerprint) {
		if (!fingerprint) return false;
		const prev = recentSubmissions.get(fingerprint);
		if (prev && (Date.now() - prev) < DEDUP_WINDOW_MS) {
			const secsAgo = Math.round((Date.now() - prev) / 1000);
			otherError = `You already submitted this file ${secsAgo} seconds ago — it's processing. Check the History Portal.`;
			return true;
		}
		return false;
	}

	function getFormatId(style, weekEnding) {

		if (style === 'old DLSE PWC Style') {
			if (weekEnding === 'Friday') return 1; // && fileFormat === 'TreeRoot PDF/A'
		}

		// PWC Style mappings
		if (style === 'PWC Style') {
			if (weekEnding === 'Friday') return 2;
			if (weekEnding === 'Saturday') return 3;
			if (weekEnding === 'Sunday') return 4;
		}
		
		// LCP Style mapping (week-ending doesn't matter)
		if (style === 'LCP Style') {
			if (weekEnding === 'Friday') return 5; //ADCO and ADCO
			if (weekEnding === 'Sunday') return 5; //Firestop
		}

		// WH-347 Style mapping (week-ending doesn't matter)
		if (style === 'WH-347 Style') {
			if (weekEnding === 'Tuesday') return 6; //Trimms
			if (weekEnding === 'Friday' ) return 6; //ADCO
		}

		// DIR PWC Style mapping (week-ending doesn't matter)
		if (style === 'DIR PWC') {
			if (weekEnding === 'Friday' ) return 7; //
		}
		
		// All unmapped combinations
		return 0;
	}

	function estimateProcessingTime(file, isOCR) {
		if (!file) return '';
		const textPages = Math.max(1, Math.round(file.size / 6600));
		const imgPages = Math.max(1, Math.round(file.size / 150000));
		const minPerPage = isOCR ? 0.1 : 0.028; // OCR is ~0.1 min/page, text extraction is ~0.03 min/page
		const vmOverhead = 2;
		const textMin = Math.ceil(vmOverhead + (textPages * minPerPage));
		const imgMin = Math.ceil(vmOverhead + (imgPages * minPerPage));
		return `Text PDF: ~${textMin} min (~${textPages} pages) | Image PDF: ~${imgMin} min (~${imgPages} pages)`;
	}


	function resetForm(clearStatus = true) {
		formatStyle = "";
		weekEnding = "Friday";
		targetAgency = "";
		targetOrganization = "";
		formatWarning = "";
		uploadedFile = null;
		formatTest = false;
		useOCR = false;
		formatStyleError = "";
		targetOrganizationError = "";
		otherError = "";
		if (clearStatus) {
			uploadStatus = "idle";
			uploadMessage = "";
		}
	}

	async function pollUploadStatus(searchId) {
		const maxAttempts = 120;
		const pollInterval = 10000;
		
		for (let attempt = 0; attempt < maxAttempts; attempt++) {
			try {
				const resp = await fetch(`/api/v1/db-core/upload/status?search_id=${searchId}`);
				if (resp.ok) {
					const data = await resp.json();
					if (data.status === 'success' && data.search_status === 'finished') {
						uploadStatus = "success";
						uploadMessage = "Upload successful!";
						// Reset only after confirmed success; keep status visible
						setTimeout(() => {
							db_form_desktop?.reset();
							db_form_mobile?.reset();
							resetForm(false);
						}, 3000);
						return;
					}
					if (data.status === 'error') {
						uploadStatus = "error";
						uploadMessage = data.error || "Upload failed!";
						return;
					}
				}
			} catch (e) {
				console.log(`Poll attempt ${attempt + 1}: ${e.message}`);
			}
			await new Promise(resolve => setTimeout(resolve, pollInterval));
		}
		uploadStatus = "error";
		uploadMessage = "Upload timeout!";
	}


	const apiEndpoint = 'db-core/upload'
	async function postFormData_on_desktop_click(event) {
		event.preventDefault()

		if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
			otherError = "Please choose a PDF file to upload";
			return;
		}
		otherError = "";

		if (!targetOrganization || targetOrganization.trim() === '') {
			targetOrganizationError = "Contractor name is required";
			return;
		}
		targetOrganizationError = "";
		
		// Get the format ID from the three selections
    	let formatId = getFormatId(formatStyle, weekEnding);

		// Check if format is supported
		if (formatId === 0) {
			formatWarning = "Format combination not supported - please contact PADG to request this format";
			return; // Don't proceed with upload
		}
		
		formatWarning = ""; // Clear any previous warning

		const fingerprint = getSubmissionFingerprint(fileInput.files, formatStyle, targetOrganization);
		if (checkDuplicate(fingerprint)) return;
		recentSubmissions.set(fingerprint, Date.now());

    	
		//let target_style_index1 = ecpr_style_options.findIndex(x => x.name === targetStyle) + 1;
		//let target_style_index1 = ecpr_style_options.find(x => x.name === targetStyle); -- delete later
		//let target_style_index1_str = target_style_index1.id.toString().padStart(2, '0') -- delete later
		let target_style_index1_str = formatId.toString().padStart(2, '0')


		uploadStatus = "pending";
    	uploadMessage = "Uploading... Watch History Portal for Upload Progress";
		targetAgency = "WDL"
		//console.log('pdf style prefix: ', target_style_index1_str);

		for (const file of fileInput.files) {

			const fileId = fileInput.files[0].name; // Or however you generate filename-UUID

			postUploadData(apiEndpoint, file, target_style_index1_str, targetOrganization, targetAgency, fileType, formatTest, useOCR)
				.then((response) => response.json())
				.then((response) => {
					console.log("Response:", response); 
					
					// Handle async processing (202 status)
					if (response.status === 'processing' && response.search_id) {
    					pollUploadStatus(response.search_id);
						return;
					}
					
					// Handle immediate success/failure
					if (response.message == 'Upload successful') {
						uploadStatus = "success";
						uploadMessage = "Upload successful!";
						// Reset only after confirmed success; keep status visible
						setTimeout(() => {
							db_form_desktop?.reset();
							resetForm(false);
						}, 3000);
					} else {
						uploadStatus = "error";
						uploadMessage = response.message || "Upload failed!";
					}
				});
		}
	}


	async function postFormData_on_mobile_click(event) {
		event.preventDefault()

		if (!fileInput_m || !fileInput_m.files || fileInput_m.files.length === 0) {
			otherError = "Please choose a PDF file to upload";
			return;
		}
		otherError = "";

		if (!targetOrganization || targetOrganization.trim() === '') {
			targetOrganizationError = "Contractor name is required";
			return;
		}
		targetOrganizationError = "";

		// Get the format ID from the three selections
    	let formatId = getFormatId(formatStyle, weekEnding);

		// Check if format is supported
		if (formatId === 0) {
			formatWarning = "Format combination not supported - please contact PADG to request this format";
			return; // Don't proceed with upload
		}

		formatWarning = ""; // Clear any previous warning
    
		//let target_style_index2 = string(ecpr_style_options.findIndex(x => x.name === targetStyle) + 1); -- delete later
		//let target_style_index2_str = target_style_index2.toString().padStart(2, '0') -- delete later
		let target_style_index2_str = formatId.toString().padStart(2, '0')

		uploadStatus = "pending";
    	uploadMessage = "Uploading... Watch History Portal for Upload Progress";
		targetAgency = "WDL"

		const fileId = fileInput_m.files[0].name;

		postUploadData(apiEndpoint, fileInput_m.files[0], target_style_index2_str, targetOrganization, targetAgency, fileType, formatTest, useOCR)
			.then((response) => response.json())
			.then((response) => {
				console.log("Response:", response);

				// Handle async processing (202 status)
				if (response.status === 'processing' && response.file_id) {
					pollUploadStatus(response.file_id);
					return;
				}

				// Handle immediate success/failure
				if (response.message == 'Upload successful') {
					uploadStatus = "success";
					uploadMessage = "Upload successful!";
					// Reset only after confirmed success; keep status visible
					setTimeout(() => {
						db_form_mobile?.reset(); //https://stackoverflow.com/questions/31063919/uncaught-typeerror-cannot-read-property-reset-of-null-in-javascript
						resetForm(false);
					}, 3000);
				} else {
					uploadStatus = "error";
					uploadMessage = response.message || "Upload failed!";
				}
			});
	}

	

	async function postFormData_test_download(event) {
		event.preventDefault();
		formatTest = true;
		useOCR = false;

		// Check which file input has a file and call appropriate handler
		if (fileInput && fileInput.files && fileInput.files.length > 0) {
			await postFormData_on_desktop_click(event);
		} else if (fileInput_m && fileInput_m.files && fileInput_m.files.length > 0) {
			await postFormData_on_mobile_click(event);
		} else {
			otherError = "Please choose a PDF file to upload";
			formatTest = false;
			return;
		}
		
		formatTest = false;
	}

	async function postFormData_deeptest_download(event) {
		event.preventDefault();
		formatTest = true;
		useOCR = true

		// Check which file input has a file and call appropriate handler
		if (fileInput && fileInput.files && fileInput.files.length > 0) {
			await postFormData_on_desktop_click(event);
		} else if (fileInput_m && fileInput_m.files && fileInput_m.files.length > 0) {
			await postFormData_on_mobile_click(event);
		} else {
			otherError = "Please choose a PDF file to upload";
			formatTest = false;
			return;
		}
		
		formatTest = false;
		useOCR = false
	}



</script>

<main class=background>
	<Navbar/>
	<div class="flex flex-col space-y-3 mt-6 items-center lg:flex-row lg:justify-center">
		<div class="mx-2 !mb-4 px-10 py-6 max-w-4xl w-full flex-1 card">
			
			
			<!-- Use hidden to preserve form state instead of discarding -->

			<!--Desktop -->
			<div class="hidden md:block"> <!-- Cheap way out, especially since layout change on desktop-->
				<p class="text-2xl font-semibold text-blue-900 mb-4">db Core</p>
				<p class="text-sm text-gray-600">
					Add Investigation Payroll Record PDFs to your Private db Core.
				</p>
				<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>
				
				<form id="ecprPost" method="POST" class="h-full relative" enctype="multipart/form-data" bind:this={db_form_desktop} on:submit|preventDefault={postFormData_on_desktop_click} >
					
					<div class="flex flex-col space-y-3 text-xl">

						<div class='form'>
							<input type='file' id='file-upload' name='file_upload' bind:this={fileInput} actions='?/upload' accept='application/pdf' multiple />
						</div>

						<!-- Format Warning Message -->
						{#if formatWarning !== ''}
						<div class="text-red-600 font-semibold text-lg p-3 bg-red-50 border border-red-300 rounded">
							{formatWarning}
						</div>
						{/if}

						<div class='input w-full'>
							<div class="relative">

								<select id="format-style" name="format_style" bind:value={formatStyle} 
										class="input-field mt-4 w-full {formatStyleError === '' ? '' : 'border-red-600'}">
									<option value="">Select a Format Style</option>
									{#each format_styles as style}
									<option value={style}>{style}</option>
									{/each}
								</select>
								<label for="format-style" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-5 scale-[.65] top-3 z-10 origin-[0] left-2.5">
									Payroll Form Style
								</label>

								<p class="text-sm text-gray-600 mb-3">
									See <a href="/faq" class="text-blue-600 hover:underline">FAQ</a> for format examples
								</p>
							</div>
							<p class='error-text {formatStyleError === '' ? 'hidden' : ''}'>{formatStyleError}</p>
						</div>
						

						<!-- Organization Input (SEPARATE from format style) -->
						<div class='input w-full'>
							<div class="relative">
								<input id="target-organization" name="target_organization" bind:value={targetOrganization} 
									placeholder="Name of Contractor or Subcontractor" 
									class="w-full peer z-20 placeholder:text-transparent {targetOrganizationError === '' ? '' : 'border-red-600'}"/>
								<label for="target-organization" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">
									Name of Contractor or Subcontractor
								</label>
							</div>
							<p class="error-text {targetOrganizationError === '' ? 'hidden' : ''}">{targetOrganizationError}</p>
						</div>
						

						<!-- Week Ending Radio Buttons -->
						<!--
						<div class='input w-full'>
							<fieldset>
								<legend class="text-gray-700 font-medium mb-2">Week Ending Day</legend>

								<p class="text-sm text-gray-600">
									Use Friday by default if your specific day is not available.
								</p>

								<div class="flex space-x-6">
									{#each week_ending_options as day}
									<label class="flex items-center space-x-2 cursor-pointer">
										<input type="radio" name="week_ending" bind:group={weekEnding} value={day} 
											class="w-4 h-4 text-blue-600" />
										<span>{day}</span>
									</label>
									{/each}
								</div>
							</fieldset>
							<p class='error-text {weekEndingError === '' ? 'hidden' : ''}'>{weekEndingError}</p>
						</div>
						-->
						

						<p class="text-sm text-gray-600">
							Upload uses fast text extraction (~80% accuracy). Deep Test uses optional OCR for ~95%+ accuracy (~0.1 min/page). Poor quality files require OCR which needs the OCR subscription to activate.
						</p>
						{#if !privateCPRFeature}
						<p class="text-red-600 font-semibold">Private database is not available for your plan. Contact support@paloaltodatagroup.com to activate.</p>
						{/if}


						<div class="relative inline-block">
							<!-- Main button group -->
							<div class="flex">
								<!-- Upload button -->
								<button type='submit'
									disabled={uploadStatus === "pending" || !privateCPRFeature}
									class="w-auto px-6 pb-2 pt-1 mt-4 text-xl rounded-l-md bg-blue-500 disabled:bg-gray-400 disabled:text-white hover:bg-blue-700 cursor-pointer disabled:cursor-not-allowed text-white font-semibold border-none shadow-sm">
									Upload
								</button>

								<!-- Dropdown toggle -->
								<button type='button'
									disabled={uploadStatus === "pending" || !privateCPRFeature}
									on:click={() => showUploadDropdown = !showUploadDropdown}
									class="px-3 pb-2 pt-1 mt-4 text-xl rounded-r-md bg-blue-500 disabled:bg-gray-400 hover:bg-blue-700 text-white font-semibold border-none border-l border-blue-400 shadow-sm disabled:cursor-not-allowed">
									▼
								</button>
							</div>
							
							<!-- Dropdown menu -->
							{#if showUploadDropdown}
							<div class="absolute z-10 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
								<button type='button'
									on:click={(e) => { showUploadDropdown = false; postFormData_test_download(e); }}
									class="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100 rounded-md">
									Test Upload
								</button>
								<button type='button'
									on:click={(e) => { showUploadDropdown = false; postFormData_deeptest_download(e); }}
									class="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100 rounded-md">
									Deep Test
								</button>
							</div>
							{/if}
						</div>

						{#if uploadStatus === "pending"}
							<p>{uploadMessage}</p>
							<p class="text-sm text-gray-600">Estimated: {estimateProcessingTime(fileInput?.files?.[0], useOCR)}</p>
						{/if}

						{#if uploadStatus === "success"}
							<p>{uploadMessage}</p>
							{#if uploadedFile}
								<p>Uploaded file: {uploadedFile.filename}</p>
							{/if}
						{/if}

						{#if uploadStatus === "error"}
							<p style="color:red;">{uploadMessage}</p>
						{/if}

					</div>
				</form>

			</div>

			<!--Mobile -->
			<div class="block md:hidden"> 
				<p class="text-2xl font-semibold text-blue-900 mb-4">db Core</p>
				<p class="text-sm text-gray-600">
					Add Investigation Payroll Record PDFs to your Private db Core.
				</p>
				<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>

				<form id="ecprPostm" method="POST" class="h-full relative" enctype="multipart/form-data" bind:this={db_form_mobile} on:submit|preventDefault={postFormData_on_mobile_click} >
					
					<div class="flex flex-col space-y-3 text-xl">

						<div class='form'>
							<input type='file' id='file-upload' name='file_upload' bind:this={fileInput_m} actions='?/upload' accept='application/pdf' />
						</div>

						<!-- Format Warning Message -->
						{#if formatWarning !== ''}
						<div class="text-red-600 font-semibold text-lg p-3 bg-red-50 border border-red-300 rounded">
							{formatWarning}
						</div>
						{/if}

						<div class='input'>
							<div class="relative">

								<select id="format-style" name="format_style" bind:value={formatStyle} 
										class="input-field mt-4 w-full {formatStyleError === '' ? '' : 'border-red-600'}">
									<option value="">Select a Format Style</option>
									{#each format_styles as style}
									<option value={style}>{style}</option>
									{/each}
								</select>
								<label for="format-style" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-5 scale-[.65] top-3 z-10 origin-[0] left-2.5">
									Payroll Form Style
								</label>

								<p class="text-sm text-gray-600 mb-3">
									See <a href="/faq" class="text-blue-600 hover:underline">FAQ</a> for format examples
								</p>

							</div>
						</div>

						<!-- Organization Input (SEPARATE from format style) -->
						<div class='input w-full'>
							<div class="relative">
								<input id="target-organization-mobile" name="target_organization" bind:value={targetOrganization} 
									placeholder="Name of Contractor or Subcontractor" 
									class="w-full peer z-20 placeholder:text-transparent {targetOrganizationError === '' ? '' : 'border-red-600'}"/>
								<label for="target-organization-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">
									Name of Contractor or Sub
								</label>
							</div>
							<p class="error-text {targetOrganizationError === '' ? 'hidden' : ''}">{targetOrganizationError}</p>
						</div>
						

						<!-- Week Ending Radio Buttons -->
						<!--
						<div class='input w-full'>
							<fieldset>
								<legend class="text-gray-700 font-medium mb-2">Week Ending Day</legend>

								<p class="text-sm text-gray-600">
									Use Friday by default.
								</p>
								
								<div class="flex flex-wrap gap-4">
									{#each week_ending_options as day}
									<label class="flex items-center space-x-2 cursor-pointer">
										<input type="radio" name="week_ending" bind:group={weekEnding} value={day} 
											class="w-4 h-4 text-blue-600" />
										<span>{day}</span>
									</label>
									{/each}
								</div>
							</fieldset>
							<p class='error-text {weekEndingError === '' ? 'hidden' : ''}'>{weekEndingError}</p>
						</div>
						-->
						

						<p class="text-sm text-gray-600">
							Upload uses fast text extraction (~80% accuracy). Deep Test uses optional OCR for ~95%+ accuracy (~0.1 min/page). Poor quality files require OCR which needs the OCR subscription to activate.
						</p>
						{#if !privateCPRFeature}
						<p class="text-red-600 font-semibold">Private database is not available for your plan. Contact support@paloaltodatagroup.com to activate.</p>
						{/if}

						<div class="relative inline-block">
							<!-- Main button group -->
							<div class="flex">
								<!-- Upload button -->
								<button type='submit'
									disabled={uploadStatus === "pending" || !privateCPRFeature}
									class="w-auto px-6 pb-2 pt-1 mt-4 text-xl rounded-l-md bg-blue-500 disabled:bg-gray-400 disabled:text-white hover:bg-blue-700 cursor-pointer disabled:cursor-not-allowed text-white font-semibold border-none shadow-sm">
									Upload
								</button>

								<!-- Dropdown toggle -->
								<button type='button'
									disabled={uploadStatus === "pending" || !privateCPRFeature}
									on:click={() => showUploadDropdown = !showUploadDropdown}
									class="px-3 pb-2 pt-1 mt-4 text-xl rounded-r-md bg-blue-500 disabled:bg-gray-400 hover:bg-blue-700 text-white font-semibold border-none border-l border-blue-400 shadow-sm disabled:cursor-not-allowed">
									▼
								</button>
							</div>
							
							<!-- Dropdown menu -->
							{#if showUploadDropdown}
							<div class="absolute z-10 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
								<button type='button'
									on:click={(e) => { showUploadDropdown = false; postFormData_test_download(e); }}
									class="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100 rounded-md">
									Test Upload
								</button>
								<button type='button'
									on:click={(e) => { showUploadDropdown = false; postFormData_deeptest_download(e); }}
									class="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100 rounded-md">
									Deep Test
								</button>
							</div>
							{/if}
						</div>

						{#if uploadStatus === "pending"}
							<p>{uploadMessage}</p>
							<p class="text-sm text-gray-600">Estimated: {estimateProcessingTime(fileInput_m?.files?.[0], useOCR)}</p>
						{/if}

						{#if uploadStatus === "success"}
							<p>{uploadMessage}</p>
							{#if uploadedFile}
								<p>Uploaded file: {uploadedFile.filename}</p>
							{/if}
						{/if}

						{#if uploadStatus === "error"}
							<p style="color:red;">{uploadMessage}</p>
						{/if}

					</div>	
				</form>
			</div>

		</div>
	</div>
	<Footer/>
</main>

<style>
	main.background { 
		background-color:theme("colors.gray.100");
	}
	.input-field {
		@apply rounded-md shadow-sm font-thin py-1 px-2;
	}
	.error-text {
		color: #dc2626;
		font-size: 0.875rem;
		margin-top: 0.25rem;
	}
</style>